import '@fontsource-variable/roboto'
import '@/common/utils/deploymentManager' // Initialize deployment version tracking

import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import { I18nextProvider } from 'react-i18next'
import Script from 'react-load-script'
import { Provider } from 'react-redux'
import { KindeProvider } from '@kinde-oss/kinde-auth-react'
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import { PostHogProvider } from '@posthog/react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { SnackbarProvider } from 'notistack'
import posthog from 'posthog-js'
import { PersistGate } from 'redux-persist/integration/react'

import { API_BASE_URL } from '@/common/config/api'

import { persistor, store } from './app/store'
import { ErrorPageContainer, PageSpinner } from './common/components'
import { reactPlugin } from './common/utils'
import App from './App'
import i18n from './i18n'

// Optional Connected account configuration
let stripeOptions = {}
if (import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID && import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID !== '') {
    stripeOptions = {
        stripeAccount: import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID,
    }
}

// Stripe configuration
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, stripeOptions)

const ELEMENTS_OPTIONS = {
    // Locale for localized error messages
    locale: 'auto',

    // Customize appearance to match your theme
    appearance: {
        theme: 'stripe', // or 'night', 'flat'
        variables: {
            fontFamily: 'Roboto, system-ui, sans-serif',
        },
    },

    // For better performance
    fonts: [
        {
            cssSrc: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
        },
    ],
}

// Google Tag Manager

const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GTM_ID,
}

TagManager.initialize(tagManagerArgs)

const rootElement = document.getElementById('root')

const onError = () => (
    <ErrorPageContainer
        linkHref="/"
        linkText="Back to your dashboard"
        subtitle="It's not you, it's us, please try that again, or contact support for help."
        title="Something went wrong"
    />
)

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2025-05-24',
    capture_exceptions: true,
    debug: import.meta.env.MODE === 'development',
})

createRoot(rootElement).render(
    <StrictMode>
        <PostHogProvider client={posthog}>
            <Suspense fallback={<PageSpinner />}>
                <Provider store={store}>
                    <PersistGate
                        loading={<PageSpinner labelText="Initializing..." />}
                        persistor={persistor}
                    >
                        <AppInsightsErrorBoundary
                            appInsights={reactPlugin}
                            onError={onError}
                        >
                            <AppInsightsContext.Provider value={reactPlugin}>
                                <Elements
                                    options={ELEMENTS_OPTIONS}
                                    stripe={stripePromise}
                                >
                                    <I18nextProvider i18n={i18n}>
                                        <SnackbarProvider
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            }}
                                            autoHideDuration={2500}
                                            maxSnack={3}
                                            preventDuplicate
                                        >
                                            <KindeProvider
                                                audience={API_BASE_URL}
                                                callbacks={{
                                                    onSuccess: (_user, state, _context) => {
                                                        if (state?.redirectTo) {
                                                            window.location = state.redirectTo
                                                        }
                                                    },
                                                }}
                                                clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
                                                domain={import.meta.env.VITE_KINDE_DOMAIN}
                                                logoutUri={window.location.origin}
                                                redirectUri={window.location.origin}
                                                useInsecureForRefreshToken={process.env.NODE_ENV === 'development'}
                                            >
                                                <Script
                                                    attributes={{ defer: true, async: true }}
                                                    url={`https://maps.googleapis.com/maps/api/js?key=${
                                                        import.meta.env.VITE_GOOGLE_API_KEY
                                                    }&libraries=places&loading=async`}
                                                />
                                                <App />
                                            </KindeProvider>
                                        </SnackbarProvider>
                                    </I18nextProvider>
                                </Elements>
                            </AppInsightsContext.Provider>
                        </AppInsightsErrorBoundary>
                    </PersistGate>
                </Provider>
            </Suspense>
        </PostHogProvider>
    </StrictMode>
)
